import { useEffect, useId, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { m } from 'motion/react';
import { X } from 'lucide-react';
import { isTopDialog, useDialog } from '../hooks/useDialog';
import { useT } from '../hooks/useT';

interface ModalProps {
  title: string;
  onClose: () => void;
  /** Enter bevestigt (bv. "Overnemen"); laat weg voor dialogen zonder primaire actie */
  onConfirm?: () => void;
  /** Breder venster voor lijsten (geschiedenis, ranglijst) */
  size?: 'md' | 'lg';
  /** Vaste onderbalk onder de scrollbare inhoud */
  footer?: ReactNode;
  /** Zonder sluitknop en zonder sluiten bij klik ernaast (bv. leg gewonnen: kies een actie) */
  dismissible?: boolean;
  children: ReactNode;
}

/** Basis-dialoog: portal naar body, focus-trap, Escape sluit, klik op de achtergrond sluit. */
export function Modal({ title, onClose, onConfirm, size = 'md', footer, dismissible = true, children }: ModalProps) {
  const { t } = useT();
  const titleId = useId();
  const dialogRef = useDialog(true);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (!isTopDialog(dialogRef.current)) return;
      if (e.key === 'Escape' && dismissible) onClose();
      if (e.key === 'Enter' && onConfirm && !(e.target instanceof HTMLButtonElement)) onConfirm();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, onConfirm, dismissible, dialogRef]);

  return createPortal(
    <m.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.15 }}
      className="fixed inset-0 z-[120] grid place-items-center bg-slate-950/50 p-4 backdrop-blur-sm"
      onClick={dismissible ? onClose : undefined}
    >
      <m.div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.2 }}
        className={`flex max-h-[90dvh] w-full flex-col rounded-3xl bg-slate-50 p-6 text-slate-900 shadow-xl outline-none dark:border dark:border-white/10 dark:bg-slate-900 dark:text-slate-100 ${
          size === 'lg' ? 'max-w-lg' : 'max-w-md'
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4 flex items-center justify-between gap-3">
          <h2 id={titleId} className="text-lg font-bold">{title}</h2>
          {dismissible && (
            <button
              onClick={onClose}
              aria-label={t('closeBtn')}
              className="grid h-11 w-11 shrink-0 place-items-center rounded-2xl bg-black/5 transition-all duration-150 active:scale-95 dark:bg-slate-800/80"
            >
              <X size={20} aria-hidden />
            </button>
          )}
        </div>
        <div className="-mx-1 min-h-0 flex-1 overflow-y-auto px-1">{children}</div>
        {footer && <div className="mt-4 border-t border-black/5 pt-4 dark:border-white/10">{footer}</div>}
      </m.div>
    </m.div>,
    document.body,
  );
}
